import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import Dropdown from 'flarum/common/components/Dropdown';
import Link from 'flarum/common/components/Link';
import collectDescendants from './utils/tagTree';
import registerTagAttributes from './utils/tagAttributes';

// Replaces the plain-text forum title in the header (hidden via CSS - see
// #home-link/.Header-title in forum.less, there's no logo configured so it
// would otherwise just be raw text) with a dropdown listing every
// forum/category: primary tags (the migrated phpBB forum categories, e.g.
// "Champignons") as unclickable group headers, and their secondary tags
// (the actual selectable subforums - see categorySelect.js) as links.
// Already scoped to what the current user is allowed to see:
// TagResource::scope() filters GET /api/tags server-side (whereVisibleTo),
// and app.tagList is the same source flarum-tags itself uses for its own
// tag list/filter UI, so no extra permission handling is needed here.
export default function addForumsMenu() {
  registerTagAttributes();

  // Until it resolves, app.store.all('tags') only has whatever tags
  // happened to be pulled in as `included` resources by other, unrelated
  // requests already in flight (the forum payload's 6 primary tags, tags on
  // whichever discussions the current page's own listing loaded, etc.) -
  // that set is different on every page and race-dependent on network
  // timing, which is exactly the "different list depending on the page /
  // sometimes incomplete" behaviour this was producing. Gating on a loaded
  // flag instead of reading the store directly makes the menu render
  // nothing (briefly) rather than a random partial list, then the same
  // complete list everywhere for the rest of the session.
  let loaded = false;
  let triggered = false;
  extend(HeaderPrimary.prototype, 'items', function (items) {
    // Triggered from here (first render) rather than eagerly at the top of
    // this function: Application.boot() only sets app.forum *after* it
    // runs every initializer (this one included), so calling
    // app.tagList.load() - which needs app.forum.attribute('apiUrl') to
    // build the request URL - at initializer time crashed with "Cannot
    // read properties of undefined (reading 'attribute')". Rendering
    // always happens later, once boot() reaches mount().
    if (!triggered) {
      triggered = true;
      app.tagList.load(['parent']).then(() => {
        loaded = true;
        m.redraw();
      });
    }
    items.add('home', m(Link, {
      className: "Button Button--link",
      href: app.route('index')
    }, app.translator.trans('champis-forum.forum.forums_menu.home_link')), 110);
    items.add('forumsMenu', m(Dropdown, {
      buttonClassName: "Button Button--link",
      menuClassName: "ChampisForumsMenu-menu",
      label: app.translator.trans('champis-forum.forum.forums_menu.label'),
      accessibleToggleLabel: app.translator.trans('champis-forum.forum.forums_menu.toggle_accessible_label')
    }, m(Link, {
      className: "ChampisForumsMenu-item",
      href: app.route('tags')
    }, app.translator.trans('champis-forum.forum.forums_menu.forums_link')), loaded ? forumsMenuItems() : m("span", {
      className: "ChampisForumsMenu-loading"
    }, app.translator.trans('champis-forum.forum.forums_menu.loading'))), 85);
  });
}
function forumsMenuItems() {
  const tags = app.store.all('tags');
  const items = [];
  tags.filter(tag => !tag.parent()).sort((a, b) => (a.position() ?? 0) - (b.position() ?? 0)).forEach(root => {
    // Any depth, not just direct children - this hierarchy sometimes
    // goes 3 levels (e.g. "Rédactions" -> "Liens" -> "Ascomycètes").
    // Sorted by the original phpBB display order (see champisOrder in
    // extend.php) rather than flarum-tags' own position, which is only
    // ever set for primary tags.
    const descendants = collectDescendants(tags, root, (a, b) => a.champisOrder() - b.champisOrder());

    // Skip categories with nothing under them (e.g. the unused default
    // "General" tag) - a header with no links beneath it isn't useful.
    if (!descendants.length) return;
    items.push(m("span", {
      key: `group-${root.id()}`,
      className: "ChampisForumsMenu-group"
    }, root.name()));
    descendants.forEach(_ref => {
      let {
        tag,
        depth
      } = _ref;
      items.push(m(Link, {
        key: tag.id(),
        className: "ChampisForumsMenu-item",
        style: {
          paddingLeft: `${20 + (depth - 1) * 15}px`
        },
        href: app.route('tag', {
          tags: tag.slug()
        })
      }, tag.name()));
    });
  });
  return items;
}